import { defineMessages, useIntl } from 'react-intl';

import Button, { LinkButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import AppSwitcherIcon from '@atlaskit/icon/core/app-switcher';
import CreditCardIcon from '@atlaskit/icon/core/credit-card';
import MagicWandIcon from '@atlaskit/icon/core/magic-wand';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import ModalDialog, { ModalHeader } from '@atlaskit/modal-dialog';
import { Anchor, Box, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { urls } from 'src/urls';
import { publishUiEvent } from 'src/utils/analytics/publish';

import bgImgUrl from './background.svg';
import { FeatureBullet } from './feature-bullet';

const modalHeight = '586px';

const scrollContainerStyles = xcss({
  backgroundImage: `url(${bgImgUrl})`,
  backgroundRepeat: 'no-repeat',
  height: modalHeight,
  overflowY: 'auto',
  position: 'relative',
  width: '100%',
});

const headerStyles = xcss({
  position: 'absolute',
  width: '100%',
});

const contentStyles = xcss({
  background: token('elevation.surface.overlay'),
  display: 'flex',
  flexDirection: 'column',
  float: 'right',
  gap: 'space.200',
  height: modalHeight,
  maxWidth: '484px', // half of the modal at full width
  overflowY: 'auto', // only necessary at smaller widths when content is taller than modal height
  paddingLeft: 'space.300',
  paddingRight: 'space.600',
  paddingTop: 'space.600',
  width: '100%', // allow content to fill the modal (overlapping the bg image at smaller widths)
});

const featureListStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  gap: 'space.200',
  listStyleType: 'none',
  margin: '0',
  marginLeft: 'space.200',
  padding: '0',
});

const learnMoreStyles = xcss({
  fontWeight: 'font.weight.bold',
  textDecoration: 'none',
  marginTop: 'space.100',
});

const controlStyles = xcss({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'space.100',
  marginLeft: 'space.200',
  marginTop: 'space.100',
});

const messages = defineMessages({
  headingText: {
    id: 'frontbucket.modals.nonDismissibleUnlinkedWorkspace.headingText',
    description: 'Text for heading of unlinked workspace modal',
    defaultMessage: 'Get your workspace ready for powerful new features',
  },
  blurbText: {
    id: 'frontbucket.modals.nonDismissibleUnlinkedWorkspace.blurbText',
    description: 'Text for content of unlinked workspace modal',
    defaultMessage:
      'Connecting this workspace to an Atlassian organization only takes a minute and will eventually be mandatory. Connect now to avoid future disruptions.',
  },
  firstInLineText: {
    id: 'frontbucket.modals.nonDismissibleUnlinkedWorkspace.firstInLineText',
    description: 'Text for first in line message of unlinked workspace modal',
    defaultMessage: 'You’ll be first in line for:',
  },

  bulletRovoDevTitle: {
    id: 'frontbucket.modals.nonDismissibleUnlinkedWorkspace.bulletRovoDevTitle',
    description: 'Title for Rovo Dev bullet point of unlinked workspace modal',
    defaultMessage: 'Rovo Dev AI',
  },
  bulletRovoDevDesc: {
    id: 'frontbucket.modals.nonDismissibleUnlinkedWorkspace.bulletRovoDevDesc',
    description:
      'Description for Rovo Dev bullet point of unlinked workspace modal',
    defaultMessage: 'Automate tasks and accelerate development',
  },
  bulletPackageRegTitle: {
    id: 'frontbucket.modals.nonDismissibleUnlinkedWorkspace.bulletPackageRegTitle',
    description:
      'Title for Package Registry bullet point of unlinked workspace modal',
    defaultMessage: 'Package registry',
  },
  bulletPackageRegDesc: {
    id: 'frontbucket.modals.nonDismissibleUnlinkedWorkspace.bulletPackageRegDesc',
    description:
      'Description for Package Registry bullet point of unlinked workspace modal',
    defaultMessage: 'Centralized package management',
  },
  bulletCentralizedUMTitle: {
    id: 'frontbucket.modals.nonDismissibleUnlinkedWorkspace.bulletCentralizedUMTitle',
    description:
      'Title for Centralized User Management bullet point of unlinked workspace modal',
    defaultMessage: 'Centralized user management',
  },
  bulletCentralizedUMDesc: {
    id: 'frontbucket.modals.nonDismissibleUnlinkedWorkspace.bulletCentralizedUMDesc',
    description:
      'Description for Centralized User Management bullet point of unlinked workspace modal',
    defaultMessage: 'Manage user access in one place',
  },
  bulletUnifiedBillingTitle: {
    id: 'frontbucket.modals.nonDismissibleUnlinkedWorkspace.bulletUnifiedBillingTitle',
    description:
      'Title for Unified Billing bullet point of unlinked workspace modal',
    defaultMessage: 'Unified billing',
  },
  bulletUnifiedBillingDesc: {
    id: 'frontbucket.modals.nonDismissibleUnlinkedWorkspace.bulletUnifiedBillingDesc',
    description:
      'Description for Unified Billing bullet point of unlinked workspace modal',
    defaultMessage: 'Simplify billing across all Atlassian products',
  },
  learnMoreText: {
    id: 'frontbucket.modals.nonDismissibleUnlinkedWorkspace.learnMoreText',
    description: 'Text for learn more link of unlinked workspace modal',
    defaultMessage: 'See instructions in our community post',
  },
  linkWsText: {
    id: 'frontbucket.modals.nonDismissibleUnlinkedWorkspace.linkWsText',
    description: 'Text for link workspace button of unlinked workspace modal',
    defaultMessage: 'Connect this workspace',
  },
  feedbackLinkText: {
    id: 'frontbucket.modals.nonDismissibleUnlinkedWorkspace.feedbackLinkText',
    description: 'Text for feedback link of unlinked workspace modal',
    defaultMessage: 'Share feedback',
  },
});

export const UnlinkedWorkspaceFeaturelistModal = ({
  onLinkWsClick,
  onFeedbackLinkClick,
  onClose,
}: {
  onLinkWsClick: () => void;
  onFeedbackLinkClick: () => void;
  onClose: () => void;
}) => {
  const intl = useIntl();

  const onLearnMoreClick = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'learn-more-unlinked-ws-featurelist-modal',
      source: 'unlinkedWorkspaceFeaturelistModal',
    });
  };

  const onCloseLocal = () => {
    onClose();

    publishUiEvent({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'close-unlinked-ws-featurelist-modal',
      source: 'unlinkedWorkspaceFeaturelistModal',
    });
  };

  return (
    <ModalDialog width="x-large" height="auto" onClose={onCloseLocal}>
      <Box xcss={scrollContainerStyles}>
        <Box xcss={headerStyles}>
          <ModalHeader hasCloseButton></ModalHeader>
        </Box>
        <Box xcss={contentStyles}>
          <Heading size="medium">
            {intl.formatMessage(messages.headingText)}
          </Heading>

          <Text>{intl.formatMessage(messages.blurbText)}</Text>

          <Heading size="xsmall" as="h4">
            {intl.formatMessage(messages.firstInLineText)}
          </Heading>

          <Box as="ul" xcss={featureListStyles}>
            <FeatureBullet
              backgroundColor="color.background.accent.lime.subtler"
              icon={MagicWandIcon}
              iconColor={token('color.icon.accent.lime')}
              title={intl.formatMessage(messages.bulletRovoDevTitle)}
              description={intl.formatMessage(messages.bulletRovoDevDesc)}
            />

            <FeatureBullet
              backgroundColor="color.background.accent.green.subtler"
              icon={AppSwitcherIcon}
              iconColor={token('color.icon.accent.green')}
              title={intl.formatMessage(messages.bulletPackageRegTitle)}
              description={intl.formatMessage(messages.bulletPackageRegDesc)}
            />

            <FeatureBullet
              backgroundColor="color.background.accent.teal.subtler"
              icon={PeopleGroupIcon}
              iconColor={token('color.icon.accent.teal')}
              title={intl.formatMessage(messages.bulletCentralizedUMTitle)}
              description={intl.formatMessage(messages.bulletCentralizedUMDesc)}
            />

            <FeatureBullet
              backgroundColor="color.background.accent.blue.subtler"
              icon={CreditCardIcon}
              iconColor={token('color.icon.accent.blue')}
              title={intl.formatMessage(messages.bulletUnifiedBillingTitle)}
              description={intl.formatMessage(
                messages.bulletUnifiedBillingDesc
              )}
            />
          </Box>

          <Anchor
            href={urls.external.orgToWsLinkingSupportPage}
            xcss={learnMoreStyles}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onLearnMoreClick}
          >
            {intl.formatMessage(messages.learnMoreText)}
          </Anchor>

          <Box xcss={controlStyles}>
            <Button appearance="subtle" onClick={onFeedbackLinkClick}>
              {intl.formatMessage(messages.feedbackLinkText)}
            </Button>
            <LinkButton
              appearance="primary"
              href={urls.external.orgToWsLinking}
              onClick={onLinkWsClick}
              target="_blank"
              rel="noopener noreferrer"
            >
              {intl.formatMessage(messages.linkWsText)}
            </LinkButton>
          </Box>
        </Box>
      </Box>
    </ModalDialog>
  );
};
